/**
 * localStorage that never throws. Use it instead of `localStorage` everywhere.
 *
 * When a visitor blocks site data (a Chrome setting, some in-app browsers),
 * merely reading `window.localStorage` throws a SecurityError, and a render
 * that touches it takes the whole page down. `setItem` also throws when storage
 * is full. A value that can't be stored is kept in memory instead, so the
 * visitor can still log in and translate; it is gone on reload.
 *
 * On the server every read returns null and every write does nothing: a
 * module-level fallback there would be shared between visitors.
 */

// Keys whose last write didn't reach localStorage.
const memory = new Map<string, string>();

function local(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

const isBrowser = () => typeof window !== "undefined";

export const storage = {
  getItem(key: string): string | null {
    if (!isBrowser()) return null;
    if (memory.has(key)) return memory.get(key)!;
    try {
      return local()?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },

  setItem(key: string, value: string): void {
    if (!isBrowser()) return;
    try {
      const store = local();
      if (store) {
        store.setItem(key, value);
        memory.delete(key);
        return;
      }
    } catch {
      // Full, or blocked: fall through to memory.
    }
    memory.set(key, value);
  },

  removeItem(key: string): void {
    if (!isBrowser()) return;
    memory.delete(key);
    try {
      local()?.removeItem(key);
    } catch {
      // Nothing stored there to remove.
    }
  },

  keys(): string[] {
    if (!isBrowser()) return [];
    let stored: string[] = [];
    try {
      const store = local();
      if (store) stored = Object.keys(store);
    } catch {
      // Blocked: only the in-memory keys exist.
    }
    return [...new Set([...stored, ...memory.keys()])];
  },
};
